import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import type { ComponentPropsWithoutRef, ElementType, MouseEvent } from 'react';
import type { NavigationCardProps } from '../NavigationCard';
import { Typography, type TypographyProps } from '../Typography';
import type { TypographyFontSize } from '../Typography/types';

export interface InlineButtonProps<T extends ElementType>
  extends TypographyProps {
  as?: T;
  onClick?: (event: MouseEvent) => void;
  truncateWidth?: number;
  to?: string;
  fontSize?: TypographyFontSize;
  disabled?: boolean;
  faded?: boolean;
  underline?: boolean;
}

export const InlineLink = <T extends ElementType = 'a'>({
  as,
  onClick,
  ellipsis,
  label,
  color = 'text-ds-natural-600',
  truncateWidth,
  fontSize = 'text-ds-400',
  disabled,
  faded,
  underline = true,
  ...props
}: InlineButtonProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof NavigationCardProps<T>>) => {
  const [hovered, setHover] = useBoolean();
  const Component = as || 'a';
  const {
    fontFamily,
    fontWeight,
    letterSpacing,
    lineHeight,
    verticalTextAlign,
    ...rest
  } = props;

  return (
    <Component
      className={classNames({
        'pointer-events-none cursor-default': disabled,
        'pointer-events-auto cursor-pointer': !disabled,
        'opacity-75': hovered && faded,
      })}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        if (onClick && !disabled) {
          onClick(event);
        }
      }}
      {...rest}
      onMouseEnter={() => {
        !disabled && setHover.setTrue();
      }}
      onMouseLeave={() => {
        !disabled && setHover.setFalse();
      }}
    >
      <div
        className="truncate"
        style={{ maxWidth: `${truncateWidth}px` }}
      >
        <Typography
          underline={underline}
          ellipsis={ellipsis}
          color={
            disabled
              ? 'text-ds-natural-300'
              : faded
                ? color
                : hovered && props.to
                  ? 'text-ds-primary'
                  : color
          }
          label={label}
          tagName="span"
          fontSize={fontSize}
          lineHeight={lineHeight || 'leading-ds-300'}
          fontWeight={fontWeight || 'font-ds-normal'}
          fontFamily={fontFamily}
          letterSpacing={letterSpacing}
          verticalTextAlign={verticalTextAlign}
        />
      </div>
    </Component>
  );
};
